import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  catchError,
  filter,
  first,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { Constants } from 'src/app/core/constants';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { EmployeeProfileView } from 'src/app/core/models/company/employee/employee-profile-view.interface';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import {
  PermissionFormModel,
  PermissionsFormModel,
} from 'src/app/core/models/permission/permission-form-group.model';
import { Permission } from 'src/app/core/models/permission/permission.interface';
import { AuthService } from 'src/app/core/services/auth.service';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { FileService } from 'src/app/core/services/file.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ProfileService } from 'src/app/core/services/profile.service';
import { ReorderedEnums } from 'src/app/core/utils/reordered-enums';
import { Util } from 'src/app/core/utils/util';

@Component({
    selector: 'app-employee-profile',
    templateUrl: './employee-profile.component.html',
    styleUrls: ['./employee-profile.component.scss'],
    providers: [PermissionsFormModel],
    standalone: false
})
export class EmployeeProfileComponent implements OnInit {
  formModel = inject(PermissionsFormModel);
  private readonly authService = inject(AuthService);
  private readonly profileService = inject(ProfileService);
  private readonly employeeService = inject(EmployeeService);
  private readonly fileService = inject(FileService);
  private readonly permissionService = inject(PermissionService);
  private readonly modalService = inject(NgbModal);
  private readonly translateService = inject(TranslateService);

  @Input({ required: true }) id!: string;
  @Input({ required: true }) companyId!: string;
  @Input() showDeleteButton = true;
  @Input() showPermissions = false;
  @Output() employeeDeleted = new EventEmitter<void>();

  public readonly notFound = Constants.NotFound;
  public readonly permissionsEnum = UserCompanyPermissions;
  public readonly resourceTypesEnum = ResourceTypes;

  public orderedResourceTypes = ReorderedEnums.enumMap.get(
    Constants.ResourceTypes,
  ) as
    | {
        key: string;
        value: ResourceTypes;
        formArray: FormArray<FormGroup<PermissionFormModel>>;
      }[]
    | undefined;

  public profileView$!: Observable<EmployeeProfileView>;
  public permissions$ = new BehaviorSubject<Permission[]>([]);

  public canDeleteEmployee$ = this.authService.user$.pipe(
    map((user) => user.data?.id !== this.id),
  );

  ngOnInit(): void {
    this.employeeService.setEmployeesUrl(this.companyId);

    const profile$ = this.profileService.getProfileById(this.id).pipe(
      switchMap((profile) => {
        if (!profile.avatarImageExtension) {
          profile.imageUrl = Constants.NotFound;
          return of(profile);
        }

        return this.fileService.getEntityWithImageUrl(
          profile,
          `users/${profile.id}/avatar${profile.avatarImageExtension}`,
        );
      }),
    );

    const employeeMeta$ = this.employeeService.getEmployees().pipe(
      map((employees) => employees.find((employee) => employee.id === this.id)),
    );

    this.profileView$ = combineLatest([profile$, employeeMeta$]).pipe(
      map(([profile, employee]) => ({
        profile,
        joinedAt: employee?.joinedAt,
      })),
    );

    this.permissionService.setPermissionsUrl(this.companyId);

    this.profileView$
      .pipe(
        first(),
        switchMap(({ profile }) =>
          this.permissionService.getUserCompanyPermissions(profile.id),
        ),
        tap((permissions) => {
          this.formModel.init(permissions);

          if (this.orderedResourceTypes) {
            for (const obj of this.orderedResourceTypes) {
              const formArray = this.getFormArrayByType(obj.value);

              obj.formArray = formArray;
            }
          }

          permissions.forEach((permission) => {
            const formArray = this.getFormArrayByType(permission.resourceType);

            const formGroup = formArray.controls.find(
              (g) => g.controls.meta.value.id === permission.id,
            );

            if (formGroup) {
              formGroup.controls.granted.setValue(true);
            }
          });

          this.permissions$.next(permissions);
        }),
      )
      .subscribe();
  }

  public deleteEmployee(): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'співробітника',
        en: 'employee',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => this.employeeService.deleteEmployee(this.id)),
        tap(() => this.employeeDeleted.emit()),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onToggle(permissionFormGroup: FormGroup<PermissionFormModel>): void {
    const { meta, granted } = permissionFormGroup.getRawValue();

    if (granted) {
      this.permissionService
        .grantPermission({
          ...meta,
          userId: this.id,
        })
        .pipe(
          first(),
          tap((permission) => {
            permissionFormGroup.patchValue({ meta: permission });
          }),
        )
        .subscribe();
    } else {
      this.permissionService
        .revokePermission(meta.id)
        .pipe(first())
        .subscribe();
    }
  }

  private getFormArrayByType(
    resourceType: ResourceTypes,
  ): FormArray<FormGroup<PermissionFormModel>> {
    switch (resourceType) {
      case ResourceTypes.Company:
        return this.formModel.form.controls.company;
      case ResourceTypes.Project:
        return this.formModel.form.controls.project;
      case ResourceTypes.Assignment:
        return this.formModel.form.controls.assignment;
      case ResourceTypes.Employee:
        return this.formModel.form.controls.employee;
      case ResourceTypes.Contact:
        return this.formModel.form.controls.contact;
      case ResourceTypes.Organization:
        return this.formModel.form.controls.organization;
    }
  }
}
